import * as HLF from "html-living-framework";
import * as PIXI from "pixi.js";
import {Ease} from "../core/Animatable";
import {Container} from "../core/Container";
import {Callback} from "../core/Reflection";
import {Poly, Shape} from "../core/Shape";
import {Times} from "../core/Signal";
import {Assets} from "../data/Assets";
import {DefaultMatch2screenData, IMatch2ScreenData} from "../data/IMatch2ScreenData";
import {MaxLevel, MinLevel} from "../data/LevelModel";
import {Location, Locations} from "../data/Location";
import {CardNumber, isCardExcluded, LocationCardType, MaxCardNumber, MinCardNumber} from "../data/LocationCardType";
import {Match2Model} from "../data/Match2Model";
import {PersistentGameModel} from "../data/PersistentGameModel";
import {
    getLocationFromVO,
    getLocationTileVO,
    voMatchKudos,
    voOptionsDirections,
    voPostcardCompleteKudos,
} from "../data/VoiceOver";
import {Button, ButtonHome} from "../gameobjects/Button";
import {ButtonMute} from "../gameobjects/ButtonMute";
import {CardTransform} from "../gameobjects/CardTransform";
import {IdleVO} from "../gameobjects/IdleVO";
import {Keyboard} from "../gameobjects/Keyboard";
import {LocationBackground} from "../gameobjects/LocationBackground";
import {Luna} from "../gameobjects/Luna";
import {Match2Board} from "../gameobjects/Match2Board";
import {Match2Card} from "../gameobjects/Match2Card";
import {SparkleBurst, SparkleBurstLarge} from "../gameobjects/SparkleBurst";
import {FreePlayLayout_main} from "../layout/FreePlayLayout";
import {LocationBackgroundLayout_main} from "../layout/LocationBackgroundLayout";
import {Analytics} from "../managers/Analytics";
import {AudioChannel} from "../managers/AudioChannel";
import {VoiceOverManager} from "../managers/VoiceOverManager";
import {AbstractGameState} from "./AbstractGameState";
import {SelectLocationScreen} from "./SelectLocationScreen";

export class FreePlayScreen extends AbstractGameState {

    public static readonly NAME = "FreePlayScreen";

    public readonly main = this.addChild(new Container());
    public readonly background = this.main.addChild(new LocationBackground());
    public readonly buttonHome = this.addChild(new ButtonHome("button-home-round", () => {
        this.gotoNextState(SelectLocationScreen.NAME, {...this._screenData});
    }));

    public burstContainer = this.main.addChild(new Container());
    public board!: Match2Board<LocationCardType>;
    public luna = this.main.addChild(new Luna(0.7));

    public readonly buttonContinue = this.main.addChild(new Button(undefined, () => {
        this.showHomeButton();
        this.hideButtons(() => {
            this.newBoard(() => {
                this.startIdleInterval();
            });
        });
    }));
    public readonly buttonComplete = this.main.addChild(new ButtonHome(undefined, () => {
        this.hideButtons(() => {
            this.gotoNextState(SelectLocationScreen.NAME, {...this._screenData});
        });
    }));
    public readonly buttonMute = this.addChild(new ButtonMute());
    public readonly cardInHat = new CardTransform();
    public readonly sparklePosition = {x: 0, y: 0};
    public readonly hatMask: number[] = [];
    public readonly burstArea = {x: 0, y: 0, width: 0, height: 0};

    private _screenData!: IMatch2ScreenData;
    private _isBoardComplete: boolean = false;
    private _movingCards: number = 0;
    private _hatMask!: Shape;
    private readonly _idleVO = new IdleVO("patw_g02", "patw_g03", "patw_g04", "patw_ui10");

    public init(pSize: PIXI.Point, pData?: IMatch2ScreenData) {
        super.init(pSize);

        this._screenData = pData ?? {...DefaultMatch2screenData};

        this.main.scale.set(0.5);

        LocationBackgroundLayout_main.call(this);
        this.background.setLocation(this._screenData.location);
        this.background.zoomIn();

        this.buttonHome.visible = false;
        this.newBoard(() => {
            this.buttonHome.visible = true;
            this.buttonHome.moveYFrom(-this._size.y / 2 - this.buttonHome.height, 0.4, Ease.backOut);
            Keyboard.add(
                this.buttonHome,
                this.buttonMute);
        });

        this.luna.moveYFrom(this.luna.y + this.luna.characterSpine.height, 0.2, Ease.sineOut, {
            delay: 0.4,
            onComplete: () => {
                this.luna.idleBlink();
            },
        });

        this.buttonHome.hoverVO = "patw_ui15";
        this.buttonComplete.hoverVO = "patw_ui15";
        this.buttonContinue.hoverVO = "patw_ui18";

        PersistentGameModel.setFreePlayVisited(true);

        VoiceOverManager.instance.playVO("patw_ui10", {
            ...voOptionsDirections,
            callback: () => this.startIdleInterval(),
        });

        Analytics.event("startLevel", "bonus");
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);

        const p = 6;
        this.buttonHome.position.set(
            -this._size.x / 2 + this.buttonHome.width * 0.5 + p,
            -this._size.y / 2 + this.buttonHome.height * 1.15);

        if (this.buttonMute) {
            this.buttonMute.setPosition(this._size);
        }
    }

    public destroy(pOptions?: boolean | PIXI.DestroyOptions) {
        this._idleVO.destroy();
        super.destroy(pOptions);
    }

    private newBoard(cb?: Callback) {
        this.createBoard();

        FreePlayLayout_main.call(this);
        this.buttonContinue.visible = false;
        this.buttonComplete.visible = false;

        if (!this._hatMask) {
            this._hatMask = this.main.addChild(new Poly(this.hatMask));
            this._hatMask.alpha = 0;
        }

        this.showBoard(() => {
            Keyboard.add(...this.board.cards);
            if (cb) {
                cb();
            }
        });
    }

    private createBoard() {
        if (this.board) {
            this.board.destroy();
        }
        const totalCards = 16;
        const cardTypes = Locations.reduce((a: LocationCardType[], location: Location) => {
            for (let level = MinLevel; level <= MaxLevel; level++) {
                for (let cardNumber = MinCardNumber; cardNumber <= MaxCardNumber; cardNumber++) {
                    if (!isCardExcluded(location, level, cardNumber as CardNumber)) {
                        a.push({location, level, index: cardNumber, freePlay: true});
                    }
                }
            }
            return a;
        }, []);
        HLF.ArrayUtils.shuffle(cardTypes);
        this.board = new Match2Board(new Match2Model<LocationCardType>(totalCards, cardTypes));
        this.main.addChild(this.board);
        this.board.zIndex = this.luna.zIndex;
        this.board.onMatch.add(this.onMatch2, this);
        this.board.onComplete.add(this.onBoardComplete, this);
        this.board.onPickCard.add(() => {
            AudioChannel.SFX.play(Assets.audio.tileClick.assetName);
            this.startIdleInterval();
        });
    }

    private showBoard(cb?: Callback) {
        const indices: number[][] = [
            [0],
            [1, 4],
            [2, 5, 8],
            [3, 6, 9, 12],
            [7, 10, 13],
            [11, 14],
            [15],
        ];
        this.board.cardsEnabled = false;
        const onComplete = Times(this.board.cards.length, () => {
            this.board.cardsEnabled = true;
            if (cb) {
                cb();
            }
        });
        let d = 0;
        for (const ii of indices) {
            for (const i of ii) {
                this.board.cards[i].visible = true;
                this.board.cards[i].scaleFrom(0, 0.3, Ease.backOut, {delay: d, onComplete});
            }
            d += 0.12;
        }
    }

    private onMatch2(card1: Match2Card, card2: Match2Card) {
        const hatOffDuration = 1.6;
        const leftToRight = card1.x < card2.x;
        this.moveCard(card1, hatOffDuration, leftToRight);
        this.moveCard(card2, hatOffDuration, !leftToRight);
        const onHatOff = () => {

            const burst = this.main.addChild(new SparkleBurst());
            burst.x = this.sparklePosition.x;
            burst.y = this.sparklePosition.y;
            burst.emitter.playOnceAndDestroy();
            AudioChannel.SFX.play(Assets.audio.envelopeClick.assetName);

            this._movingCards--;
            hatOn();
        };
        const hatOn = () => {
            if (this._movingCards <= 0 && !this.luna.isTalking) {
                if (this.luna.isHatOff) {
                    this.luna.hatOn(() => {
                        this.luna.idleBlink();
                    });
                } else {
                    this.luna.idleBlink();
                }
                this.onCompleteMatch2();
            }
        };
        if (this._movingCards > 0) {
            this.luna.delayedCall(hatOffDuration, onHatOff);
        } else {
            this.luna.hatOff(hatOffDuration, onHatOff);
        }
        this._movingCards++;

        Keyboard.remove(card1, card2);
        Keyboard.focus(this.board.getClosestEnabledCard(card2) || this.buttonHome);

        this.stopIdleInterval();
        this.playMatch2VO(card1.cardType, hatOn);
    }

    private playMatch2VO(cardType: LocationCardType, cb: Callback) {
        const vo = [];
        const tileVO = getLocationTileVO(cardType.location, cardType.level, cardType.index as CardNumber);
        if (tileVO) {
            vo.push(tileVO, HLF.ArrayUtils.random(voMatchKudos), getLocationFromVO(cardType.location));
        }
        if (vo.length > 0) {
            VoiceOverManager.instance.playVO(vo, {...voOptionsDirections, callback: cb});
        } else {
            cb();
        }
    }

    private onCompleteMatch2() {
        if (this._isBoardComplete) {
            this._isBoardComplete = false;
            this.stopIdleInterval();
            this.burstSparkles();
            AudioChannel.SFX.play(Assets.audio.postcardComplete.assetName);
            if (VoiceOverManager.instance.active) {
                VoiceOverManager.instance.playVO([
                    HLF.ArrayUtils.random(voPostcardCompleteKudos),
                    "patw_g08",
                ], {
                    ...voOptionsDirections,
                    callback: () => {
                        this.gotoLocationSelect();
                    },
                });
            } else {
                this.main.delayedCall(2, () => this.gotoLocationSelect());
            }
            // No complete screen, go directly back to the location select
            // this.showButtons();
        } else {
            this.startIdleInterval();
        }
    }

    private onBoardComplete() {
        this._isBoardComplete = true;
        Analytics.event("endLevel", "bonus");
    }

    private burstSparkles() {
        const burst = this.burstContainer.addChild(new SparkleBurstLarge());
        burst.x = this.burstArea.x + HLF.Random.floatBetween(0, this.burstArea.width);
        burst.y = this.burstArea.y + HLF.Random.floatBetween(0, this.burstArea.height);
        burst.scale.set(4);
        burst.emitter.playOnceAndDestroy();
        this.board.delayedCall(0.8, () => this.burstSparkles());
    }

    private moveCard(card: Match2Card, duration: number, left: boolean, cb?: Callback) {
        const pulseDuration = card.pulse(1, () => {
            duration -= pulseDuration;
            card.zIndex = -1;
            card.changeParent(this.main);
            card.delayedCall(duration * 0.7, () => {
                card.mask = this._hatMask;
            });
            const shadow = card.shadow;
            const destination = this.cardInHat.getTransformBackup();
            shadow.changeParent(card.parent);
            shadow.zIndex = this.luna.zIndex;
            card.tweenTo(duration, Ease.sineInOut, {
                bezier: {
                    type: "soft",
                    values: [
                        {x: card.x * 0.2 + destination.x * 0.8, y: Math.min(card.y - card.height, -this._size.y * 0.5)},
                        {x: destination.x, y: destination.y},
                    ],
                },
                scaleX: destination.scaleX,
                scaleY: destination.scaleY,
                rotation: (left ? -1 : 1) * 2 * Math.PI,
                onComplete: () => {
                    card.visible = false;
                    card.mask = null;
                    card.changeParent(this.board);
                    shadow.changeParent(card);
                    shadow.zIndex = 0;
                    if (cb) {
                        cb();
                    }
                },
            });
            shadow.alphaTo(0, 0.4);
        });
    }

    private showButtons(cb?: Callback) {
        this.buttonComplete.visible = true;
        this.buttonContinue.visible = true;
        this.buttonComplete.enabled = true;
        this.buttonContinue.enabled = true;
        this.buttonHome.enabled = false;
        this.buttonHome.saveTransform();
        this.buttonHome.moveYTo(-this._size.y / 2 - this.buttonHome.height, 0.2, Ease.sineIn, () => {
            this.buttonHome.visible = false;
        });
        this.buttonComplete.moveYFrom(this._size.x + this.buttonComplete.height, 0.4, Ease.backOut);
        this.buttonContinue.moveYFrom(this._size.x + this.buttonContinue.height, 0.4, Ease.backOut, {
            delay: 0.2,
            onComplete: cb,
        });
        Keyboard.add(
            this.buttonComplete,
            this.buttonContinue);
        Keyboard.focus(this.buttonComplete);
    }

    private hideButtons(cb?: Callback) {
        this.buttonComplete.enabled = false;
        this.buttonContinue.enabled = false;
        this.buttonComplete.moveYTo(this._size.x + this.buttonComplete.height, 0.2, Ease.sineIn);
        this.buttonContinue.moveYTo(this._size.x + this.buttonContinue.height, 0.2, Ease.sineIn, cb);
        Keyboard.remove(
            this.buttonComplete,
            this.buttonContinue);
    }

    private showHomeButton() {
        this.buttonHome.enabled = true;
        this.buttonHome.visible = true;
        this.buttonHome.moveYTo(this.buttonHome.getTransformBackup().y, 0.4, Ease.backOut);
    }

    private gotoLocationSelect() {
        this.buttonHome.moveYTo(-this._size.y / 2 - this.buttonHome.height, 0.2, Ease.sineIn);
        this.luna.moveYTo(this.luna.y + this.luna.characterSpine.height, 0.2, Ease.sineIn, () => {
            this.gotoNextState(SelectLocationScreen.NAME, {...this._screenData});
        });
    }

    private startIdleInterval() {
        this._idleVO.start();
    }

    private stopIdleInterval() {
        this._idleVO.stop();
    }

    private randomClickUIStabilityTesting() {
        setInterval(() => {
            const card = HLF.ArrayUtils.random(this.board.cards.filter((it) => it.enabled));
            if (card && card.enabled) {
                card.onClick(card);
            }
        }, 100);
    }
}
